import { v4 as uuidv4 } from 'uuid';
import { MealType } from '@/types/meal-plan';
import { EnhancedFoodItem, SimilarProfile } from './types';

// Expanded meal alternatives database with more variety
export const ENHANCED_MEAL_ALTERNATIVES: Record<MealType, EnhancedFoodItem[]> = {
  breakfast: [
    {
      id: 'alt-breakfast-1',
      name: 'Upma with Vegetables',
      calories: 200,
      protein: 6,
      carbs: 38,
      fat: 4,
      micronutrients: { vitaminA: 100, vitaminC: 20, calcium: 25, iron: 3 },
      serving: '150g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'gluten-free'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['fiber-rich', 'complex carbs', 'vegetable protein'],
      healthGoalTags: ['weight-management', 'digestive-health'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-breakfast-2',
      name: 'Idli with Sambhar',
      calories: 180,
      protein: 8,
      carbs: 30,
      fat: 2,
      micronutrients: { vitaminA: 80, vitaminC: 25, calcium: 40, iron: 4 },
      serving: '200g',
      clientFrequency: 9,
      dietaryTags: ['vegetarian', 'fermented', 'low-fat'],
      regionCuisine: 'South Indian',
      reasoningKeywords: ['probiotic', 'easy digestion', 'complete protein'],
      healthGoalTags: ['weight-management', 'gut-health', 'muscle-building'],
      similarProfileFrequency: 10
    },
    {
      id: 'alt-breakfast-3',
      name: 'Oats with Nuts and Berries',
      calories: 220,
      protein: 8,
      carbs: 35,
      fat: 6,
      micronutrients: { vitaminA: 90, vitaminC: 35, calcium: 60, iron: 3 },
      serving: '150g',
      clientFrequency: 7,
      dietaryTags: ['vegetarian', 'high-fiber'],
      regionCuisine: 'International',
      reasoningKeywords: ['antioxidants', 'heart-healthy', 'sustained energy'],
      healthGoalTags: ['heart-health', 'weight-management'],
      similarProfileFrequency: 8
    },
    {
      id: 'alt-breakfast-4',
      name: 'Moong Dal Cheela',
      calories: 190,
      protein: 12,
      carbs: 25,
      fat: 3,
      micronutrients: { vitaminA: 70, vitaminC: 15, calcium: 30, iron: 4 },
      serving: '180g',
      clientFrequency: 6,
      dietaryTags: ['vegetarian', 'high-protein', 'gluten-free'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['lean protein', 'low glycemic', 'muscle building'],
      healthGoalTags: ['muscle-building', 'weight-loss', 'pcos-friendly'],
      similarProfileFrequency: 7
    },
    {
      id: 'alt-breakfast-5',
      name: 'Scrambled Eggs with Toast',
      calories: 210,
      protein: 15,
      carbs: 20,
      fat: 8,
      micronutrients: { vitaminA: 120, vitaminC: 5, calcium: 50, iron: 2 },
      serving: '160g',
      clientFrequency: 5,
      dietaryTags: ['high-protein', 'complete protein'],
      regionCuisine: 'International',
      reasoningKeywords: ['complete amino acids', 'choline', 'brain health'],
      healthGoalTags: ['muscle-building', 'brain-health'],
      similarProfileFrequency: 6
    },
    {
      id: 'alt-breakfast-6',
      name: 'Poha with Peanuts',
      calories: 195,
      protein: 5,
      carbs: 35,
      fat: 5,
      micronutrients: { vitaminA: 95, vitaminC: 18, calcium: 28, iron: 3.5 },
      serving: '145g',
      clientFrequency: 9,
      dietaryTags: ['vegetarian', 'gluten-free', 'quick-prep'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['light breakfast', 'easy digestion', 'traditional'],
      healthGoalTags: ['weight-management', 'quick-energy'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-breakfast-7',
      name: 'Greek Yogurt with Granola',
      calories: 230,
      protein: 12,
      carbs: 28,
      fat: 8,
      micronutrients: { vitaminA: 85, vitaminC: 20, calcium: 180, iron: 2 },
      serving: '170g',
      clientFrequency: 6,
      dietaryTags: ['vegetarian', 'probiotic', 'calcium-rich'],
      regionCuisine: 'Mediterranean',
      reasoningKeywords: ['gut health', 'protein rich', 'bone health'],
      healthGoalTags: ['gut-health', 'bone-health', 'muscle-building'],
      similarProfileFrequency: 7
    }
  ],
  morningSnack: [
    {
      id: 'alt-snack-1',
      name: 'Mixed Nuts and Dried Fruits',
      calories: 150,
      protein: 6,
      carbs: 20,
      fat: 8,
      micronutrients: { vitaminA: 100, vitaminC: 40, calcium: 80, iron: 1.5 },
      serving: '30g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'portable', 'energy-dense'],
      regionCuisine: 'International',
      reasoningKeywords: ['healthy fats', 'sustained energy', 'convenient'],
      healthGoalTags: ['energy-boost', 'heart-health'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-snack-2',
      name: 'Greek Yogurt with Berries',
      calories: 120,
      protein: 10,
      carbs: 15,
      fat: 3,
      micronutrients: { vitaminA: 80, vitaminC: 25, calcium: 150, iron: 0.5 },
      serving: '150g',
      clientFrequency: 9,
      dietaryTags: ['vegetarian', 'probiotic', 'high-protein'],
      regionCuisine: 'Mediterranean',
      reasoningKeywords: ['gut health', 'calcium rich', 'antioxidants'],
      healthGoalTags: ['gut-health', 'bone-health', 'weight-management'],
      similarProfileFrequency: 8
    },
    {
      id: 'alt-snack-3',
      name: 'Banana with Peanut Butter',
      calories: 160,
      protein: 7,
      carbs: 22,
      fat: 7,
      micronutrients: { vitaminA: 60, vitaminC: 15, calcium: 20, iron: 1 },
      serving: '120g',
      clientFrequency: 7,
      dietaryTags: ['vegetarian', 'pre-workout'],
      regionCuisine: 'International',
      reasoningKeywords: ['quick energy', 'potassium', 'muscle recovery'],
      healthGoalTags: ['pre-workout', 'muscle-recovery', 'energy-boost'],
      similarProfileFrequency: 8
    },
    {
      id: 'alt-snack-4',
      name: 'Roasted Makhana (Fox Nuts)',
      calories: 135,
      protein: 5,
      carbs: 18,
      fat: 4,
      micronutrients: { vitaminA: 45, vitaminC: 8, calcium: 90, iron: 2 },
      serving: '35g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'low-calorie', 'crunchy'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['light snack', 'calcium rich', 'satisfying crunch'],
      healthGoalTags: ['weight-loss', 'bone-health', 'low-calorie'],
      similarProfileFrequency: 7
    }
  ],
  lunch: [
    {
      id: 'alt-lunch-1',
      name: 'Dal Rice with Mixed Vegetables',
      calories: 350,
      protein: 12,
      carbs: 60,
      fat: 6,
      micronutrients: { vitaminA: 120, vitaminC: 30, calcium: 50, iron: 5 },
      serving: '250g',
      clientFrequency: 9,
      dietaryTags: ['vegetarian', 'complete meal', 'comfort food'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['complete protein', 'balanced macros', 'traditional'],
      healthGoalTags: ['balanced-nutrition', 'comfort-food', 'digestive-health'],
      similarProfileFrequency: 10
    },
    {
      id: 'alt-lunch-2',
      name: 'Grilled Chicken with Quinoa',
      calories: 380,
      protein: 28,
      carbs: 32,
      fat: 12,
      micronutrients: { vitaminA: 150, vitaminC: 15, calcium: 35, iron: 6 },
      serving: '200g',
      clientFrequency: 6,
      dietaryTags: ['high-protein', 'lean meat', 'superfood'],
      regionCuisine: 'International',
      reasoningKeywords: ['lean protein', 'complete amino acids', 'nutrient dense'],
      healthGoalTags: ['muscle-building', 'weight-loss', 'high-protein'],
      similarProfileFrequency: 7
    },
    {
      id: 'alt-lunch-3',
      name: 'Chickpea Curry with Brown Rice',
      calories: 340,
      protein: 15,
      carbs: 55,
      fat: 8,
      micronutrients: { vitaminA: 110, vitaminC: 25, calcium: 60, iron: 6 },
      serving: '240g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'high-fiber', 'plant-protein'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['plant protein', 'fiber rich', 'filling'],
      healthGoalTags: ['weight-management', 'digestive-health', 'plant-based'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-lunch-4',
      name: 'Rajma Rice Bowl',
      calories: 365,
      protein: 14,
      carbs: 58,
      fat: 7,
      micronutrients: { vitaminA: 105, vitaminC: 22, calcium: 55, iron: 7 },
      serving: '260g',
      clientFrequency: 7,
      dietaryTags: ['vegetarian', 'iron-rich', 'protein-rich'],
      regionCuisine: 'North Indian',
      reasoningKeywords: ['kidney beans', 'iron rich', 'hearty meal'],
      healthGoalTags: ['iron-deficiency', 'muscle-building', 'comfort-food'],
      similarProfileFrequency: 8
    }
  ],
  afternoonSnack: [
    {
      id: 'alt-asnack-1',
      name: 'Roasted Chickpeas',
      calories: 140,
      protein: 7,
      carbs: 18,
      fat: 4,
      micronutrients: { vitaminA: 60, vitaminC: 10, calcium: 45, iron: 2.5 },
      serving: '40g',
      clientFrequency: 7,
      dietaryTags: ['vegetarian', 'crunchy', 'high-fiber'],
      regionCuisine: 'Mediterranean',
      reasoningKeywords: ['plant protein', 'satisfying crunch', 'low glycemic'],
      healthGoalTags: ['weight-management', 'plant-protein', 'fiber-rich'],
      similarProfileFrequency: 8
    },
    {
      id: 'alt-asnack-2',
      name: 'Apple with Almond Butter',
      calories: 155,
      protein: 6,
      carbs: 20,
      fat: 8,
      micronutrients: { vitaminA: 70, vitaminC: 25, calcium: 60, iron: 1 },
      serving: '130g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'fresh', 'portable'],
      regionCuisine: 'International',
      reasoningKeywords: ['natural sugars', 'healthy fats', 'fiber'],
      healthGoalTags: ['natural-energy', 'heart-health', 'weight-management'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-asnack-3',
      name: 'Sprouts Chaat',
      calories: 125,
      protein: 8,
      carbs: 16,
      fat: 3,
      micronutrients: { vitaminA: 85, vitaminC: 35, calcium: 40, iron: 3 },
      serving: '100g',
      clientFrequency: 6,
      dietaryTags: ['vegetarian', 'fresh', 'vitamin-rich'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['fresh sprouts', 'vitamin C rich', 'digestive'],
      healthGoalTags: ['digestive-health', 'vitamin-rich', 'weight-loss'],
      similarProfileFrequency: 7
    }
  ],
  dinner: [
    {
      id: 'alt-dinner-1',
      name: 'Grilled Fish with Steamed Vegetables',
      calories: 300,
      protein: 25,
      carbs: 15,
      fat: 12,
      micronutrients: { vitaminA: 180, vitaminC: 25, calcium: 40, iron: 3 },
      serving: '200g',
      clientFrequency: 7,
      dietaryTags: ['omega-3', 'low-carb', 'lean protein'],
      regionCuisine: 'Mediterranean',
      reasoningKeywords: ['omega-3 fatty acids', 'heart healthy', 'light dinner'],
      healthGoalTags: ['heart-health', 'weight-loss', 'brain-health'],
      similarProfileFrequency: 8
    },
    {
      id: 'alt-dinner-2',
      name: 'Paneer Tikka with Roti',
      calories: 320,
      protein: 18,
      carbs: 25,
      fat: 15,
      micronutrients: { vitaminA: 180, vitaminC: 30, calcium: 200, iron: 3 },
      serving: '180g',
      clientFrequency: 8,
      dietaryTags: ['vegetarian', 'calcium-rich', 'traditional'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['calcium rich', 'complete protein', 'satisfying'],
      healthGoalTags: ['bone-health', 'muscle-building', 'comfort-food'],
      similarProfileFrequency: 9
    },
    {
      id: 'alt-dinner-3',
      name: 'Lentil Soup with Whole Grain Bread',
      calories: 280,
      protein: 16,
      carbs: 45,
      fat: 5,
      micronutrients: { vitaminA: 100, vitaminC: 20, calcium: 70, iron: 5 },
      serving: '220g',
      clientFrequency: 6,
      dietaryTags: ['vegetarian', 'comfort food', 'warming'],
      regionCuisine: 'International',
      reasoningKeywords: ['plant protein', 'warming', 'easy digestion'],
      healthGoalTags: ['digestive-health', 'comfort-food', 'plant-protein'],
      similarProfileFrequency: 7
    },
    {
      id: 'alt-dinner-4',
      name: 'Khichdi with Ghee and Vegetables',
      calories: 290,
      protein: 10,
      carbs: 48,
      fat: 6,
      micronutrients: { vitaminA: 130, vitaminC: 28, calcium: 45, iron: 4 },
      serving: '230g',
      clientFrequency: 9,
      dietaryTags: ['vegetarian', 'comfort food', 'easy-digest'],
      regionCuisine: 'Indian',
      reasoningKeywords: ['comfort meal', 'easy digestion', 'complete nutrition'],
      healthGoalTags: ['digestive-health', 'comfort-food', 'recovery-meal'],
      similarProfileFrequency: 9
    }
  ]
};

// Mock client profiles for similar user matching
export const MOCK_SIMILAR_PROFILES: Record<string, SimilarProfile> = {
  'vegetarian-weight-loss-female-30s': {
    preferredMeals: ['idli-sambhar', 'dal-rice', 'khichdi', 'sprouts-chaat'],
    avgFrequency: 8.5,
    regionPreference: 'Indian'
  },
  'high-protein-fitness-female-20s': {
    preferredMeals: ['greek-yogurt', 'chicken-quinoa', 'eggs-toast', 'paneer-tikka'],
    avgFrequency: 7.8,
    regionPreference: 'International'
  },
  'pcos-friendly-female-30s': {
    preferredMeals: ['moong-dal-cheela', 'chickpea-curry', 'fish-vegetables', 'nuts-fruits'],
    avgFrequency: 8.2,
    regionPreference: 'Indian'
  }
};
